/**
 * Shared Components and Utilities for Inbox
 *
 * Deduplicated patterns extracted from multiple inbox files.
 * Import from this file instead of defining locally.
 */

import React from "react";
import { TooltipWrapper } from "ui";

export const UNKNOWN_USER_TOOLTIP =
  "We couldn't retrieve this user's profile information because of privacy or permission restrictions on the social network.";

/**
 * NoticeTooltip - Used for informational tooltip icons (? marks).
 * Previously duplicated in: preview-layout.tsx, post-preview.tsx
 */
export function NoticeTooltip({
  children,
  className,
  ...props
}: React.ComponentProps<typeof TooltipWrapper>) {
  return (
    <TooltipWrapper
      className={`inline-flex h-[22px] items-center ml-2.5 cursor-default ${
        className || ""
      }`}
      {...props}
    >
      {children}
    </TooltipWrapper>
  );
}

/**
 * NoticeIcon - Question mark badge inside NoticeTooltip.
 * Previously duplicated in: preview-layout.tsx, post-preview.tsx
 */
export function NoticeIcon({
  children,
  ...props
}: React.HTMLAttributes<HTMLSpanElement>) {
  return (
    <span
      className="inline-block px-[5px] py-0 rounded-[10px] bg-neutral-700 text-white cursor-default"
      {...props}
    >
      {children}
    </span>
  );
}

export function UnknownUserInfoIcon({
  className,
}: {
  className?: string;
}): JSX.Element {
  return (
    <NoticeTooltip
      tooltip={UNKNOWN_USER_TOOLTIP}
      placement="bottom"
      className={className}
    >
      <i
        aria-label="Unknown user information"
        className="fa-regular fa-circle-info text-sm text-ink-muted"
      />
    </NoticeTooltip>
  );
}

export function UnknownUserTooltip({
  children,
  ...props
}: Omit<React.ComponentProps<typeof NoticeTooltip>, "tooltip">): JSX.Element {
  return (
    <NoticeTooltip tooltip={UNKNOWN_USER_TOOLTIP} placement="bottom" {...props}>
      {children}
    </NoticeTooltip>
  );
}

/**
 * StyledTextSpan - Shared type for text parsing utilities.
 * Previously duplicated in: post-caption-handler.tsx, rich-text-message.tsx
 */
export interface StyledTextSpan {
  style: string;
  text: string;
  mentionedUrn?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  data?: any;
}

/**
 * regexMatchAll - Shared regex utility for text parsing.
 * Previously duplicated in: post-caption-handler.tsx, rich-text-message.tsx
 */
export function regexMatchAll(text: string, regex: RegExp): RegExpExecArray[] {
  let matched;
  const result: RegExpExecArray[] = [];
  do {
    matched = regex.exec(text);
    if (matched) {
      result.push(matched);
    }
  } while (matched);
  return result;
}

/**
 * buildSpansWithMatchedRegex - Shared span-building utility for text parsing.
 * Previously duplicated in: post-caption-handler.tsx, rich-text-message.tsx
 */
export function buildSpansWithMatchedRegex(
  text: string,
  matches: RegExpExecArray[],
  makeSpan: (match: RegExpExecArray) => StyledTextSpan
): StyledTextSpan[] {
  const spans: StyledTextSpan[] = [];
  for (let idx = 0; idx < matches.length; idx++) {
    const match = matches[idx];
    const prevMatch = idx > 0 ? matches[idx - 1] : undefined;
    spans.push({
      style: "normal",
      text: text.substring(
        prevMatch ? prevMatch.index + prevMatch[0].length : 0,
        match.index
      ),
    });
    spans.push(makeSpan(match));
    if (idx >= matches.length - 1) {
      spans.push({
        style: "normal",
        text: text.substring(match.index + match[0].length, text.length),
      });
    }
  }

  return spans.filter((c) => !!c.text);
}

/**
 * HashTagLink - Anchor for hashtag/link rendering in captions.
 * Previously in: post-caption-handler.tsx
 * Reusable across any caption/text rendering context.
 */
export function HashTagLink({
  children,
  ...props
}: React.AnchorHTMLAttributes<HTMLAnchorElement>) {
  return (
    <a
      className="text-blue-500 font-bold visited:text-blue-500 hover:text-blue-500 active:text-blue-500"
      {...props}
    >
      {children}
    </a>
  );
}

/**
 * MentionedSpan - Span for @mentions in captions.
 * Previously in: post-caption-handler.tsx
 */
export function MentionedSpan({
  children,
  ...props
}: React.HTMLAttributes<HTMLSpanElement>) {
  return (
    <span className="font-bold" {...props}>
      {children}
    </span>
  );
}
